import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Palette, Package } from 'lucide-react';

const fmtPrice = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(v);
};

/**
 * Compact teaser card for a "Farbe / Lack / Additiv" listing.
 * Fuchsia accent. Emphasizes color code (oem_number), manufacturer, quantity and price.
 */
export const PaintTeaserCard = ({ listing }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const photo = listing?.photos?.[0];

  return (
    <button
      type="button"
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      data-testid={`paint-teaser-${listing.listing_id}`}
      className="group relative text-left border border-fuchsia-100 dark:border-fuchsia-900/40 bg-white dark:bg-slate-900 hover:border-fuchsia-600 dark:hover:border-fuchsia-500 hover:shadow-sm transition-all duration-200 flex gap-3 p-3 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950"
    >
      <div className="relative h-20 w-20 md:h-24 md:w-24 shrink-0 overflow-hidden bg-fuchsia-50 dark:bg-fuchsia-950/30 border border-fuchsia-100 dark:border-fuchsia-900/40">
        {photo ? (
          <img
            src={photo}
            alt={listing.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-fuchsia-300 dark:text-fuchsia-700">
            <Package className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1 bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-800 dark:text-fuchsia-200 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5">
            <Palette className="h-2.5 w-2.5" />
            {t('paint.card.label', 'Farbe / Lack')}
          </span>
          {listing.oem_number && (
            <span className="font-mono text-[10px] text-fuchsia-700 dark:text-fuchsia-300 truncate" title={listing.oem_number}>
              {listing.oem_number}
            </span>
          )}
        </div>
        <h3 className="font-manrope text-sm font-semibold text-slate-900 dark:text-slate-50 mt-1 leading-snug line-clamp-2">
          {listing.title}
        </h3>
        {listing.manufacturer_name ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{listing.manufacturer_name}</p>
        ) : listing.part_number ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1 font-mono">#{listing.part_number}</p>
        ) : null}
        <div className="mt-auto pt-1 flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {listing.quantity && listing.unit ? `${listing.quantity} ${listing.unit}` : t('paint.card.single', 'Einzelpos.')}
          </span>
          <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-50 tabular-nums">
            € {fmtPrice(listing.price_eur)}
          </span>
        </div>
      </div>
    </button>
  );
};
